import { App, Alert, Button, Card, Divider, List, Popconfirm, Select, Space, Tag, Typography } from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CloseOutlined,
  RetweetOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { useMemo, useState } from 'react';
import { listenerLabel } from '../../lib/listenerName';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { errMsg } from '../../lib/errors';
import { usePermissions } from '../../auth/store';

/** `GET /admin/listeners/feed-shuffle`. */
interface FeedShuffle {
  enabled: boolean;
  /** Empty = no shuffled order has been minted. */
  seed: string;
  /** ISO timestamp of the last Shuffle, or null. */
  shuffledAt: string | null;
}

/** `GET /admin/listeners/feed-order` — the pinned buddies, first to last. */
interface PinnedListener {
  id: string;
  displayName: string | null;
  feedOrder: number | null;
  status: string;
  isSuspended: boolean;
  isDummy: boolean;
}

/** One row of `GET /admin/listeners`, for the "add a listener" picker. */
interface ListenerOption {
  id: string;
  displayName: string | null;
  handle?: string | null;
  /** The paged endpoint nests the handle under `user`. */
  user?: { handle?: string | null } | null;
}

const KEY = ['admin', 'listeners', 'feed-shuffle'];
const PIN_KEY = ['admin', 'listeners', 'feed-order'];

/** `ids` with `id` moved one place up (-1) or down (+1); unchanged at an edge. */
function move(ids: string[], id: string, direction: -1 | 1): string[] {
  const from = ids.indexOf(id);
  const to = from + direction;
  if (from < 0 || to < 0 || to >= ids.length) return ids;
  const next = [...ids];
  [next[from], next[to]] = [next[to]!, next[from]!];
  return next;
}

/**
 * Settings → Buddy card order.
 *
 * Buddy cards normally arrive in the feed's usual order (dummies last, then
 * rating and response rate, with HOT buddies on top). Shuffle replaces that
 * with a mixed order so the same few listeners are not always at the top.
 *
 * Deliberately NOT automatic: the order changes only when this button is
 * pressed, and then stays put — so a user scrolling does not see cards jump
 * around, and pagination never repeats or skips a buddy. It is presentation
 * only; who is online, who is busy and who gets a call are untouched.
 */
export function BuddyFeedOrderCard() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { can } = usePermissions();
  const canEdit = can('listeners.feed_shuffle');
  const canPin = can('listeners.feed_order');
  /** What the picker is searching for. Empty = show nothing (153 listeners). */
  const [search, setSearch] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: KEY,
    queryFn: async () => (await api.get<FeedShuffle>('/admin/listeners/feed-shuffle')).data,
  });

  const act = useMutation({
    mutationFn: async (action: 'shuffle' | 'off') =>
      (await api.post<FeedShuffle>('/admin/listeners/feed-shuffle', { action })).data,
    onSuccess: (saved, action) => {
      message.success(action === 'shuffle' ? 'Buddy cards shuffled' : 'Default order restored');
      qc.setQueryData(KEY, saved);
    },
    onError: (e) => message.error(errMsg(e, 'Could not change the buddy card order')),
  });

  const pinnedQuery = useQuery({
    queryKey: PIN_KEY,
    queryFn: async () => (await api.get<PinnedListener[]>('/admin/listeners/feed-order')).data,
    enabled: canPin,
  });
  const pinned = useMemo(() => pinnedQuery.data ?? [], [pinnedQuery.data]);
  const pinnedIds = useMemo(() => pinned.map((p) => p.id), [pinned]);

  /** Approved listeners matching the search, minus the ones already pinned. */
  const options = useQuery({
    queryKey: ['admin', 'listeners', 'pin-search', search],
    queryFn: async () =>
      (
        await api.get<{ items: ListenerOption[] }>('/admin/listeners', {
          // `page` is what switches the endpoint into its SEARCHABLE paged
          // mode. Without it the API keeps its legacy contract — a bare array
          // of the latest 50, with `q` ignored — so `.items` came back
          // undefined and every search read "No match".
          params: { q: search, status: 'approved', page: 1, pageSize: 20 },
        })
      ).data.items ?? [],
    enabled: canPin && search.trim().length >= 2,
  });

  const savePins = useMutation({
    mutationFn: (ids: string[]) => api.put('/admin/listeners/feed-order', { ids }),
    onSuccess: (_r, ids) => {
      message.success(ids.length === 0 ? 'All listeners unpinned' : 'Buddy card order saved');
      void qc.invalidateQueries({ queryKey: PIN_KEY });
    },
    onError: (e) => {
      message.error(errMsg(e, 'Could not save the pinned order'));
      void qc.invalidateQueries({ queryKey: PIN_KEY });
    },
  });

  const on = data?.enabled === true && data.seed.length > 0;

  return (
    <Card
      title={
        <Space>
          <span>Buddy card order</span>
          {!isLoading && <Tag color={on ? 'purple' : 'default'}>{on ? 'Shuffled' : 'Default'}</Tag>}
        </Space>
      }
      loading={isLoading}
    >
      {error ? (
        <Alert type="error" showIcon message="Could not load the buddy card order" />
      ) : (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            {on
              ? 'Users see the buddy cards in a mixed order. It stays the same for everyone until you shuffle again.'
              : 'Users see the usual order — HOT buddies first, then by rating and response rate.'}
          </Typography.Text>

          {data?.shuffledAt && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Last shuffled {new Date(data.shuffledAt).toLocaleString()}
            </Typography.Text>
          )}

          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            This changes the order of the cards only. Who is online, who is busy, who is
            LIVE and who receives a call are not affected.
          </Typography.Text>

          <Space wrap>
            <Popconfirm
              title="Shuffle the buddy cards?"
              description="Every user gets a new card order straight away. Press again for a different one."
              okText="Shuffle"
              disabled={!canEdit}
              onConfirm={() => act.mutate('shuffle')}
            >
              <Button
                type="primary"
                icon={<RetweetOutlined />}
                disabled={!canEdit}
                loading={act.isPending && act.variables === 'shuffle'}
              >
                {on ? 'Shuffle again' : 'Shuffle buddy cards'}
              </Button>
            </Popconfirm>
            <Button
              icon={<UndoOutlined />}
              disabled={!canEdit || !on}
              loading={act.isPending && act.variables === 'off'}
              onClick={() => act.mutate('off')}
            >
              Restore default order
            </Button>
          </Space>

          {!canEdit && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              You do not have permission to change the buddy card order.
            </Typography.Text>
          )}

          {canPin && (
            <>
              <Divider style={{ margin: '4px 0' }} />
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Typography.Text strong>Pinned to the top</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Lift listeners who are not getting calls. Pinned buddies show first,
                  in this order, above the HOT badge and above any shuffle. A pinned
                  listener who is offline is still hidden — pinning changes the order
                  only, never who can be called.
                </Typography.Text>

                <List
                  size="small"
                  locale={{ emptyText: 'No one pinned — the feed is in its normal order.' }}
                  dataSource={pinned}
                  loading={pinnedQuery.isLoading}
                  renderItem={(p, i) => (
                    <List.Item
                      key={p.id}
                      actions={[
                        <Button
                          key="up"
                          size="small"
                          type="text"
                          aria-label={`Move ${listenerLabel(p)} up`}
                          icon={<ArrowUpOutlined />}
                          disabled={i === 0 || savePins.isPending}
                          onClick={() => savePins.mutate(move(pinnedIds, p.id, -1))}
                        />,
                        <Button
                          key="down"
                          size="small"
                          type="text"
                          aria-label={`Move ${listenerLabel(p)} down`}
                          icon={<ArrowDownOutlined />}
                          disabled={i === pinned.length - 1 || savePins.isPending}
                          onClick={() => savePins.mutate(move(pinnedIds, p.id, 1))}
                        />,
                        <Button
                          key="remove"
                          size="small"
                          type="text"
                          danger
                          aria-label={`Unpin ${listenerLabel(p)}`}
                          icon={<CloseOutlined />}
                          disabled={savePins.isPending}
                          onClick={() => savePins.mutate(pinnedIds.filter((x) => x !== p.id))}
                        />,
                      ]}
                    >
                      <Space>
                        <Tag color="blue">{i + 1}</Tag>
                        <span>{listenerLabel(p)}</span>
                        {p.isSuspended && <Tag color="red">Suspended</Tag>}
                        {p.isDummy && <Tag>Dummy</Tag>}
                      </Space>
                    </List.Item>
                  )}
                />

                <Select
                  aria-label="Pin a listener"
                  style={{ width: '100%' }}
                  showSearch
                  value={null}
                  placeholder="Search an approved listener to pin…"
                  filterOption={false}
                  loading={options.isFetching}
                  onSearch={setSearch}
                  notFoundContent={
                    search.trim().length < 2 ? 'Type at least 2 characters' : 'No match'
                  }
                  onChange={(id: string) => {
                    if (!id || pinnedIds.includes(id)) return;
                    savePins.mutate([...pinnedIds, id]);
                    setSearch('');
                  }}
                  options={(options.data ?? [])
                    .filter((o) => !pinnedIds.includes(o.id))
                    .map((o) => ({
                      value: o.id,
                      // A listener with no Display Name falls back to their
                      // @handle, which this endpoint nests under `user`.
                      label: listenerLabel({ ...o, handle: o.handle ?? o.user?.handle ?? null }),
                    }))}
                />

                {pinned.length > 0 && (
                  <Popconfirm
                    title="Unpin everyone?"
                    description="The buddy feed goes back to its normal order."
                    okText="Unpin all"
                    onConfirm={() => savePins.mutate([])}
                  >
                    <Button size="small" danger disabled={savePins.isPending}>
                      Unpin all
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            </>
          )}
        </Space>
      )}
    </Card>
  );
}
